// Fits the timing endpoint's request ID rule (64 characters) with room for "-<question number>".
const CONVERSATION_ID_PATTERN = /^[A-Za-z0-9_-]{1,56}$/;

// Request IDs are "<Algolia conversation ID>-<question number>", so a timing row can be found in
// Agent Studio. Falls back to a random ID when there is no usable conversation ID.
export function createRequestId(conversationId = null, questionNumber = 1) {
  if (typeof conversationId === "string" && CONVERSATION_ID_PATTERN.test(conversationId) && Number.isInteger(questionNumber) && questionNumber > 0) {
    return `${conversationId}-${questionNumber}`;
  }
  return crypto.randomUUID?.() || `charli_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function createRequestLifecycle(requestId, messageIds = [], startedAtMs = performance.now()) {
  return {
    requestId,
    startedAtMs,
    durationMs: null,
    outcome: null,
    phase: "submitted",
    userMessageId: null,
    assistantMessageId: null,
    hasDisplayableContent: false,
    terminal: false,
    initialMessageIds: new Set(messageIds),
  };
}

export function transitionRequestLifecycle(lifecycle, event) {
  if (!lifecycle || !event || event.requestId !== lifecycle.requestId || lifecycle.terminal) {
    return lifecycle;
  }

  switch (event.type) {
    case "streaming":
      return { ...lifecycle, phase: "streaming" };
    case "bind-user":
      return lifecycle.userMessageId
        ? lifecycle
        : { ...lifecycle, userMessageId: event.messageId || null };
    case "bind-assistant":
      return lifecycle.assistantMessageId
        ? lifecycle
        : { ...lifecycle, assistantMessageId: event.messageId || null };
    case "content":
      return {
        ...lifecycle,
        phase: "streaming",
        hasDisplayableContent: true,
        assistantMessageId: lifecycle.assistantMessageId || event.messageId || null,
      };
    case "complete":
      return finalizeRequestLifecycle(lifecycle, "completed", event.occurredAtMs);
    case "cancel":
      return {
        ...finalizeRequestLifecycle(lifecycle, "cancelled", event.occurredAtMs),
        hasDisplayableContent: true,
      };
    case "fail":
      return {
        ...finalizeRequestLifecycle(lifecycle, "failed", event.occurredAtMs),
        hasDisplayableContent: false,
      };
    case "interrupt":
      return finalizeRequestLifecycle(lifecycle, "interrupted", event.occurredAtMs);
    default:
      return lifecycle;
  }
}

function finalizeRequestLifecycle(lifecycle, outcome, occurredAtMs) {
  const terminalAtMs = Number.isFinite(occurredAtMs) ? occurredAtMs : performance.now();
  const elapsedMs = Math.max(0, terminalAtMs - lifecycle.startedAtMs);

  return {
    ...lifecycle,
    phase: outcome,
    outcome,
    durationMs: Math.round(elapsedMs),
    terminal: true,
  };
}

export function isRequestActive(lifecycle) {
  return !!lifecycle && !lifecycle.terminal;
}

export function shouldQueueInitialObserverQuery(initialQuery, assistantMounted) {
  return !!String(initialQuery || "").trim() && !assistantMounted;
}

export function hasRequiredChatConfig(config) {
  return !!(
    config?.application_id &&
    config?.search_api_key &&
    config?.agent_id
  );
}
